import { UseMutateAsyncFunction, useMutation } from '@tanstack/react-query';
import { AxiosError, AxiosResponse } from 'axios';
import { Dispatch, SetStateAction } from 'react';

import { queryClient } from 'api';
import { API_HANDLERS } from 'api/apiHandlers';
import { alert } from 'components';
import * as ERROR_CODES from 'constants/errorCodes';
import { ApiErrors, UserProfileField } from 'types';

interface UseEditUserProfileFieldMutationReturn {
  editUserProfileFieldsMutation: UseMutateAsyncFunction<
    AxiosResponse<unknown, unknown>,
    unknown,
    UserProfileField[],
    unknown
  >;
  isLoading: boolean;
  invalidateQueries?: string[];
}

interface UseEditUserProfileFieldMutationProps {
  successText?: string;
  invalidateQueries?: string[];
  setShouldReset?: (value: boolean) => void;
  setIsSuccess?: Dispatch<SetStateAction<boolean>>;
}

export function useUpdateUserProfileFieldsMutation({
  successText,
  invalidateQueries,
  setShouldReset,
  setIsSuccess,
}: UseEditUserProfileFieldMutationProps): UseEditUserProfileFieldMutationReturn {
  const { mutateAsync: editUserProfileFieldsMutation, isLoading } = useMutation(
    API_HANDLERS.SETTINGS.UPDATE_USER_PROFILE_FIELDS,
    {
      onSuccess: () => {
        setIsSuccess && setIsSuccess(true);
        if (successText) alert.success(successText);

        if (invalidateQueries) {
          queryClient.invalidateQueries(invalidateQueries);
        }
      },

      onError: (error: AxiosError<ApiErrors>) => {
        if (error.response?.data.errors[0].code === ERROR_CODES.UPDATE_NOT_ALLOWED) {
          setShouldReset?.(true);
        }
        if (error.response?.data.errors[0].code === ERROR_CODES.INVALID_REQUEST) {
          alert.error(error.response?.data.errors[0].description);
        }
      },
    },
  );

  return {
    editUserProfileFieldsMutation,
    isLoading,
  };
}
